'use client';

import React, { useEffect, useRef, useState } from 'react';

const stats = [
  { value: 180, suffix: '+', label: 'Deals Funded', prefix: '' },
  { value: 47, suffix: 'M+', label: 'Total EMD Deployed', prefix: '$' },
  { value: 72, suffix: 'hr', label: 'Average Funding Time', prefix: '' },
  { value: 98, suffix: '%', label: 'Funding Success Rate', prefix: '' },
];

function CountUp({
  target,
  prefix,
  suffix,
  active,
}: {
  target: number;
  prefix: string;
  suffix: string;
  active: boolean;
}) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const duration = 1800;
    const start = Date.now();
    const step = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [active, target]);

  return (
    <span>
      {prefix}
      {count}
      {suffix}
    </span>
  );
}

export default function StatsBar() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="stats"
      className="relative z-10 py-16"
      style={{ background: 'var(--color-primary)' }}
      aria-label="Key statistics and metrics"
    >
      {/* Subtle dot pattern overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`text-center ${i < stats.length - 1 ? 'md:border-r md:border-white/10' : ''}`}
            >
              <p
                className="font-display font-bold leading-none mb-2"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  color: i % 2 === 0 ? 'white' : '#74C69D',
                }}
              >
                <CountUp
                  target={stat.value}
                  prefix={stat.prefix}
                  suffix={stat.suffix}
                  active={active}
                />
              </p>
              <p
                className="text-[10px] font-semibold uppercase tracking-[0.25em]"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}